import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChoiceGroup, Field } from "../Fields";
import { CollapsePresence, EASE_ARRAY, usePrefersReducedMotion } from "../calm";
import { FIELD_MAX } from "../onboardingModel";
import { StepBody, StepFooter, StepHeading } from "../StepCard";
import { numberedCount } from "../steps";
import type { StepContext } from "./shared";

type Question = "stableIds" | "labelsInstalled" | "criticalAreas" | "sourceSystem";
const QUESTIONS: Question[] = ["stableIds", "labelsInstalled", "criticalAreas", "sourceSystem"];

/** Step 7: one question at a time, as a card; the cards change with the same direction as the steps. */
export default function QuestionsStep({ ctx }: { ctx: StepContext }) {
  const { c } = ctx;
  const { t } = useTranslation();
  const reduced = usePrefersReducedMotion();
  const answers = c.values!.answers;
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const question = QUESTIONS[index];
  const indexRef = useRef(index);
  indexRef.current = index;

  // The back button (and Esc, and the left arrow) go through the cards first.
  const stepBack = useCallback(() => {
    if (indexRef.current === 0) return false;
    setDirection(-1);
    setIndex(indexRef.current - 1);
    return true;
  }, []);
  useEffect(() => {
    ctx.setBackOverride(stepBack);
    return () => ctx.setBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepBack]);

  const answered = (q: Question) => (q === "stableIds" || q === "labelsInstalled" ? !!answers[q] : true);
  const advance = () => {
    if (index < QUESTIONS.length - 1) {
      setDirection(1);
      setIndex(index + 1);
    } else ctx.next();
  };

  const offset = reduced ? 0 : 36;
  const card = () => {
    switch (question) {
      case "stableIds":
        return (
          <ChoiceGroup name="stableIds" legend={t("onboardingPage.questions.stableIds")} hint={t("onboardingPage.questions.stableIdsHint")} value={answers.stableIds} disabled={false} onChange={(value) => c.updateAnswers("stableIds", value)} />
        );
      case "labelsInstalled":
        return (
          <div className="space-y-5">
            <ChoiceGroup name="labelsInstalled" legend={t("onboardingPage.questions.labelsInstalled")} hint={t("onboardingPage.questions.labelsLead")} value={answers.labelsInstalled} disabled={false} onChange={(value) => c.updateAnswers("labelsInstalled", value)} />
            <CollapsePresence show={answers.labelsInstalled === "yes"} className="-mx-1 px-1">
              <div className="pb-1 pt-1">
                <Field id="onboarding-labelNumberNote" label={t("onboardingPage.questions.labelNumberNote")} hint={t("onboardingPage.questions.labelNumberNoteHint")} error={c.problemText("labelNumberNote", answers.labelNumberNote)}>
                  <Input id="onboarding-labelNumberNote" value={answers.labelNumberNote ?? ""} maxLength={FIELD_MAX.labelNumberNote} aria-describedby="onboarding-labelNumberNote-hint" onChange={(event) => c.updateAnswers("labelNumberNote", event.target.value)} />
                </Field>
              </div>
            </CollapsePresence>
          </div>
        );
      case "criticalAreas":
        return (
          <Field id="onboarding-criticalAreas" label={t("onboardingPage.questions.criticalAreas")} hint={t("onboardingPage.questions.criticalAreasHint")} error={c.problemText("criticalAreas", answers.criticalAreas)}>
            <Textarea id="onboarding-criticalAreas" rows={5} maxLength={FIELD_MAX.criticalAreas} value={answers.criticalAreas ?? ""} aria-describedby="onboarding-criticalAreas-hint" onChange={(event) => c.updateAnswers("criticalAreas", event.target.value)} />
          </Field>
        );
      case "sourceSystem":
        return (
          <Field id="onboarding-sourceSystem" label={t("onboardingPage.questions.sourceSystem")} hint={t("onboardingPage.questions.sourceSystemHint")} error={c.problemText("sourceSystem", answers.sourceSystem)}>
            <Input id="onboarding-sourceSystem" value={answers.sourceSystem ?? ""} maxLength={FIELD_MAX.sourceSystem} aria-describedby="onboarding-sourceSystem-hint" onChange={(event) => c.updateAnswers("sourceSystem", event.target.value)} />
          </Field>
        );
    }
  };

  return (
    <>
      <StepBody>
        <StepHeading
          eyebrow={t("onboardingPage.nav.stepOf", { current: ctx.step.index, total: numberedCount(ctx.steps) })}
          title={t("onboardingPage.titles.questions")}
          focus={ctx.focus}
          extra={
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {t("onboardingPage.questions.progress", { current: index + 1, total: QUESTIONS.length })}
              {index >= 2 && <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">{t("onboardingPage.questions.optionalBadge")}</span>}
            </p>
          }
        />
        <div className="relative rounded-2xl border border-border p-5 sm:p-6" data-testid="question-card" data-question={question}>
          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.div
              key={question}
              custom={direction}
              initial={{ opacity: 0, x: direction * offset }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -direction * offset }}
              transition={{ duration: reduced ? 0.15 : 0.32, ease: EASE_ARRAY }}
            >
              {card()}
            </motion.div>
          </AnimatePresence>
        </div>
      </StepBody>
      <StepFooter onBack={ctx.back} onNext={advance} nextDisabled={!answered(question)} nextReason={t("onboardingPage.tips.answerFirst")} />
    </>
  );
}
